import { Check } from "lucide-react";

/**
 * Choix des domaines à composer, pour les actions qui produisent un thème.
 *
 * Ce n'est pas qu'un confort d'interface : les domaines cochés sont envoyés
 * au serveur, qui n'exige QUE ceux-là dans le schéma de réponse de Gemini.
 * Décocher trois domaines sur cinq, c'est donc une réponse plus courte, un
 * appel moins cher, et surtout un thème existant qu'on ne recompose pas en
 * entier pour changer deux couleurs.
 *
 * Les clés correspondent une à une à `outputs.THEME_DOMAINS` côté serveur ;
 * les libellés sont ceux des onglets du Studio, pour que l'utilisateur
 * reconnaisse ce qu'il va voir bouger.
 */
export const THEME_DOMAINS = [
  { key: "palette", label: "Couleurs" },
  { key: "font_pair", label: "Typographie" },
  { key: "shape", label: "Forme" },
  { key: "spacing", label: "Espacement" },
  { key: "controls", label: "Contrôles" },
  // Le seul domaine qui ne touche pas au design mais à la STRUCTURE : quelles
  // sections composent la page, et dans quel ordre. Décoché, la composition
  // actuelle est intégralement conservée.
  { key: "sections", label: "Structure de la page" },
  // Les TEXTES eux-mêmes, réécrits pour l'activité décrite. Décoché, le
  // contenu déjà en place est conservé mot pour mot.
  { key: "content", label: "Textes" },
];

export const ALL_THEME_DOMAINS = THEME_DOMAINS.map((d) => d.key);

export default function AIThemeOptions({ value, onChange }) {
  const selected = value && value.length ? value : ALL_THEME_DOMAINS;

  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    // Tout décocher n'a pas de sens : la génération n'aurait plus rien à
    // produire. On garde le dernier domaine coché plutôt que d'afficher une
    // erreur au moment de cliquer sur « Générer ».
    onChange(next.length ? next : [key]);
  };

  return (
    <div data-testid="ai-theme-options">
      <label className="text-[11px] uppercase tracking-[0.15em] text-ash">À composer</label>
      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {THEME_DOMAINS.map((domain) => {
          const isOn = selected.includes(domain.key);
          return (
            <button
              key={domain.key}
              type="button"
              data-testid={`ai-theme-domain-${domain.key}`}
              onClick={() => toggle(domain.key)}
              aria-pressed={isOn}
              className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs transition-colors ${
                isOn
                  ? "border-copper bg-copper/10 text-ink"
                  : "border-stone bg-white text-graphite hover:border-copper/50 hover:text-ink"
              }`}
            >
              {isOn && <Check className="h-3 w-3" />}
              {domain.label}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-ash mt-1">
        Les domaines décochés ne sont pas régénérés : ils gardent leur réglage actuel.
      </p>
    </div>
  );
}
